import { useEffect, useState, type FC } from "react";
import { Button, Input, Modal, SelectPicker } from "rsuite";
import { Merchant } from "../../../models/sales/merchant";
import { Company } from "../../../models/company/company";
import { getCompanies } from "../../../services/company/CompanyApi";
import CoordinatePicker from "../../coordinate_picker";
import {
  District,
  Province,
  Regency,
  Village,
} from "../../../models/location/regional";
import {
  getDistricts,
  getProvinces,
  getRegencies,
  getVillages,
} from "../../../services/location/RegionalApi";
import { MerchantType } from "../../../models/sales/merchant_type";
import { getMerchantTypes } from "../../../services/sales/MerchantTypeApi";

interface MerchantFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Merchant) => void;
  merchant?: Merchant;
}

const MerchantForm: FC<MerchantFormProps> = ({
  open,
  onClose,
  onSubmit,
  merchant,
}) => {
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | null | undefined
  >(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [data, setData] = useState<Merchant>({
    id: "",
    name: "",
    phone: "",
    address: "",
  });
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [regencies, setRegencies] = useState<Regency[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [villages, setVillages] = useState<Village[]>([]);
  const [merchantTypes, setMerchantTypes] = useState<MerchantType[]>([]);

  useEffect(() => {
    if (data.regency_id) {
      getDistricts("", data.regency_id)
        .then((v) => v.json())
        .then((res) => setDistricts(res.data));
    }
  }, [data.regency_id]);

  useEffect(() => {
    if (data.district_id) {
      getVillages("", data.district_id)
        .then((v) => v.json())
        .then((res) => setVillages(res.data));
    }
  }, [data.district_id]);

  const getAllCompanies = async (search?: string) => {
    try {
      const res = await getCompanies({ page: 1, size: 10, search });
      const data = await res.json();
      setCompanies(data.data.items);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    setData(
      merchant || {
        id: "",
        name: "",
        phone: "",
        address: "",
      }
    );
    getAllCompanies();
    getProvinces("")
      .then((v) => v.json())
      .then((res) => setProvinces(res.data));

    getMerchantTypes({ page: 1, size: 100 })
      .then((v) => v.json())
      .then((res) => setMerchantTypes(res.data.items));
  }, [merchant]);

  useEffect(() => {
    if (data.province_id) {
      getRegencies("", data.province_id)
        .then((v) => v.json())
        .then((res) => setRegencies(res.data));
    }
  }, [data.province_id]);

  return (
    <Modal size={"lg"} open={open} onClose={onClose}>
      <Modal.Header>
        <Modal.Title>Merchant Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label>Company</label>
          <div className="input">
            <SelectPicker
              block
              data={companies}
              value={selectedCompanyId}
              onChange={(val) => {
                setSelectedCompanyId(`${val}`);
              }}
              placeholder="Select a company"
              valueKey="id"
              labelKey="name"
              onSearch={(val) => {
                if (val) {
                  getAllCompanies(val);
                }
              }}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Merchant Type</label>
          <div className="input">
            <SelectPicker
              block
              placeholder="e.g. Retail"
              value={data.merchant_type_id}
              data={merchantTypes}
              valueKey="id"
              labelKey="name"
              onChange={(value) =>
                setData({ ...data, merchant_type_id: value })
              }
            />
          </div>
        </div>
        <div className="form-group">
          <label>Name</label>
          <Input
            name="name"
            placeholder="e.g. Nike Retail Store"
            value={data.name}
            onChange={(value) => setData({ ...data, name: value })}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <Input
            name="phone"
            placeholder="e.g. 123-456-7890"
            value={data.phone}
            onChange={(value) => setData({ ...data, phone: value })}
          />
        </div>
        <div className="form-group">
          <label>Address</label>
          <Input
            name="address"
            as={"textarea"}
            rows={5}
            placeholder="e.g. 123 Main St, Anytown, USA 12345"
            value={data.address}
            onChange={(value) => setData({ ...data, address: value })}
          />
        </div>
        <div className="form-group">
          <label>Latitude</label>
          <Input
            name="latitude"
            placeholder="e.g. 37.7749"
            type="number"
            value={data.latitude}
            onChange={(value) =>
              setData({ ...data, latitude: parseFloat(value) })
            }
          />
        </div>
        <div className="form-group">
          <label>Longitude</label>
          <Input
            name="longitude"
            placeholder="e.g. -122.4194"
            type="number"
            value={data.longitude}
            onChange={(value) =>
              setData({ ...data, longitude: parseFloat(value) })
            }
          />
        </div>

        <div className="form-group">
          <label>Map</label>
          <div className="input">
            <CoordinatePicker
              dragging
              lat={data.latitude}
              lng={data.longitude}
              onPicker={(p) =>
                setData({ ...data, latitude: p.lat, longitude: p.lng })
              }
            />
          </div>
        </div>
        <div className="form-group">
          <label>Province</label>
          <div className="input">
            <SelectPicker
              block
              data={provinces}
              value={data.province_id}
              onChange={(val) => setData({ ...data, province_id: val })}
              placeholder="Select a province"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Regency / City</label>
          <div className="input">
            <SelectPicker
              block
              data={regencies}
              value={data.regency_id}
              onChange={(val) => setData({ ...data, regency_id: val })}
              placeholder="Select a regency"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>
        <div className="form-group">
          <label>District</label>
          <div className="input">
            <SelectPicker
              block
              data={districts}
              value={data.district_id}
              onChange={(val) => setData({ ...data, district_id: val })}
              placeholder="Select a district"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>
        <div className="form-group">
          <label>Village / Sub District</label>
          <div className="input">
            <SelectPicker
              block
              data={villages}
              value={data.village_id}
              onChange={(val) => setData({ ...data, village_id: val })}
              placeholder="Select a village / subdistrict"
              valueKey="id"
              labelKey="name"
            />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="pt-4">
          <Button
            appearance="primary"
            onClick={() => {
              if (selectedCompanyId) {
                data.company_id = selectedCompanyId;
              }
              onSubmit(data!);
              setData({
                id: "",
                name: "",
                phone: "",
                address: "",
              })
            }}
          >
            Submit
          </Button>
          <Button appearance="link" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
export default MerchantForm;
